import { auth } from '../firebase/config';

class UnitConversionService {
  constructor() {
    // Check if we're in development by looking at the URL
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    // Check for URL parameter override (e.g., ?useRemoteAPIs=true)
    const urlParams = new URLSearchParams(window.location.search);
    const useRemoteAPIs = urlParams.get('useRemoteAPIs') === 'true';
    
    const envSuffix = import.meta.env.PROD ? 'prod' : 'dev';
    this.baseUrl = (isDevelopment && !useRemoteAPIs)
      ? 'http://localhost:8000'  // Local unit-conversion API
      : `https://unit-conversion-api-${envSuffix}.blackbay-6bd764e8.eastus2.azurecontainerapps.io`;
  }

  /**
   * Get auth headers with Firebase ID token
   */
  async _getAuthHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    const user = auth.currentUser;
    if (user) {
      const idToken = await user.getIdToken();
      headers['X-Firebase-Token'] = idToken;
    }
    return headers;
  }

  async convert(keypoints, cameraParams, width, height) {
    try {
      const headers = await this._getAuthHeaders();
      const response = await fetch(`${this.baseUrl}/api/convert`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          keypoints,
          camera_params: cameraParams,
          width,
          height
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error converting pixels to meters:', error);
      throw error;
    }
  }

  async convertPoints(points, cameraParams, width, height) {
    try {
      const headers = await this._getAuthHeaders();
      const response = await fetch(`${this.baseUrl}/api/convert`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          points,
          camera_params: cameraParams,
          width,
          height
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error converting points to meters:', error);
      throw error;
    }
  }


  async convertPlayerBoundingBoxes(playerAnnotations, cameraParams, width, height) {
    try {
      const headers = await this._getAuthHeaders();
      const response = await fetch(`${this.baseUrl}/api/convert_player_bounding_boxes`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          player_annotations: playerAnnotations,
          camera_params: cameraParams,
          width,
          height
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error converting player bounding boxes to meters:', error);
      throw error;
    }
  }

  async convertMetersToPixels(realWorldKeypoints, cameraParams, width, height) {
    try {
      const headers = await this._getAuthHeaders();
      const response = await fetch(`${this.baseUrl}/api/convert_meters_to_px`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          real_world_keypoints: realWorldKeypoints,
          camera_params: cameraParams,
          width,
          height
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error converting meters to pixels:', error);
      throw error;
    }
  }

  async reprojectKeypoints(cameraParams, width, height) {
    try {
      const headers = await this._getAuthHeaders();
      const response = await fetch(`${this.baseUrl}/api/reproject_keypoints`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          camera_params: cameraParams,
          width,
          height
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Reproject keypoints API error:', {
          status: response.status,
          statusText: response.statusText,
          errorText: errorText
        });
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error reprojecting keypoints:', error);
      throw error;
    }
  }

  async healthCheck() {
    try {
      const response = await fetch(`${this.baseUrl}/health`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error checking unit-conversion API health:', error);
      throw error;
    }
  }
}

export default new UnitConversionService();
